import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { number, object, string } from "yup";
import PropTypes from "prop-types";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { ChartOfAccountTypeEnum } from "@/features/chart-of-accounts";
import { OutletChartOfAccountSelect } from "@/features/outlet-chart-of-accounts";
import {
  CustomDialogTitle,
  CustomInputField,
  CustomNumberField,
} from "@/features/ui";

const ALLOWED_OCOA_TYPES = [
  ChartOfAccountTypeEnum.REVENUE,
  ChartOfAccountTypeEnum.COST_OF_GOODS_SOLD,
  ChartOfAccountTypeEnum.EXPENSES,
  ChartOfAccountTypeEnum.OTHER_EXPENSES,
  ChartOfAccountTypeEnum.OTHER_INCOME,
  ChartOfAccountTypeEnum.OTHER_CURRENT_ASSETS,
  ChartOfAccountTypeEnum.OTHER_ASSETS,
  ChartOfAccountTypeEnum.CURRENT_LIABILITIES,
  ChartOfAccountTypeEnum.LONG_TERM_LIABILITIES,
  ChartOfAccountTypeEnum.INVENTORY,
];

const SCHEMA = object().shape({
  outletChartOfAccount: object().required("Harus diisi"),
  amount: number().min(0, "Tidak boleh negatif").required("Harus diisi"),
  note: string().nullable(),
});

export const ExpenseDialog = ({ outletId, onClose, onSubmit, defaultValues }) => {
  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(SCHEMA),
    defaultValues: {
      outletChartOfAccount: null,
      amount: 0,
      note: "",
    },
  });

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  const handleClose = () => {
    onClose();
  };

  const onFormSubmit = (data) => {
    onSubmit(data);
    handleClose();
  };

  return (
    <Dialog open onClose={handleClose} fullWidth>
      <CustomDialogTitle
        variant="h5"
        title="Tambah Biaya"
        onClose={handleClose}
      />
      <DialogContent dividers>
        <Stack
          spacing={2}
          component="form"
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Controller
                control={control}
                name="outletChartOfAccount"
                render={({ field, fieldState }) => (
                  <OutletChartOfAccountSelect
                    outletId={outletId}
                    types={ALLOWED_OCOA_TYPES}
                    value={field.value}
                    onChange={field.onChange}
                    TextFieldProps={{
                      required: true,
                      label: "Akun",
                      error: fieldState.invalid,
                      helperText: fieldState.error?.message,
                      InputProps: {
                        placeholder: "Pilih",
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                control={control}
                name="amount"
                render={({ field, fieldState }) => (
                  <CustomNumberField
                    label="Jumlah"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">Rp</InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                control={control}
                name="note"
                render={({ field, fieldState }) => (
                  <CustomInputField
                    label="Catatan"
                    value={field.value}
                    onChange={field.onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Batal</Button>
        <Button variant="contained" onClick={handleSubmit(onFormSubmit)}>
          Simpan
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ExpenseDialog.propTypes = {
  outletId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
};
